import React, { useEffect, useMemo, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import type {
  CatalogProduct,
  CreateRepairServiceTypeRequest,
  RepairDeviceType,
  RepairServiceType,
  UpdateRepairServiceTypeRequest,
  Vendor,
} from '@techgitter/contracts';

import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import { SmartSelect, type SmartSelectOption } from '../dropdown';
import { HelpHint } from './HelpHint';
import { useQuoteTermsTemplates } from '../../hooks/useQuoteTermsTemplates';
import './RepairServicePanel.css';

interface RepairServicePanelProps {
  /** Null = create mode. */
  repairServiceType: RepairServiceType | null;
  products: CatalogProduct[];
  productsLoading: boolean;
  /** Vendors used by the third-party picker. Caller pre-filters to active
   *  third-party providers, but the panel tolerates the full list and filters
   *  defensively below. */
  vendors: Vendor[];
  vendorsLoading: boolean;
  isSaving: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (
    payload: CreateRepairServiceTypeRequest | UpdateRepairServiceTypeRequest,
    id: string | null,
  ) => Promise<void>;
}

// `any` is a sentinel for the null (device-independent) device type — SmartSelect
// values are strings, so we map it back to null on change.
const ANY_DEVICE = 'any';
const DEVICE_TYPE_OPTIONS: SmartSelectOption[] = [
  { value: ANY_DEVICE, label: 'Any (device-independent)' },
  { value: 'laptop', label: 'Laptop' },
  { value: 'desktop', label: 'Desktop' },
  { value: 'all_in_one', label: 'All-in-one' },
];

/**
 * Right-aligned panel for creating / editing a repair service type. Replaces the
 * former modal dialog; lives in the right column of `RepairServicesAdminPage`'s
 * `.repair-services-layout` grid, consistent with the calendar Schedule
 * Appointment / Add Leave panels. Reuses the shared `SidePanel` shell and
 * `FormField` controls — only the page's two-column grid + a few content
 * helpers live in CSS.
 * Per-field explanatory copy is exposed via hover `HelpHint` markers rather than
 * always-visible captions, to keep the form scannable.
 */
export const RepairServicePanel: React.FC<RepairServicePanelProps> = ({
  repairServiceType,
  products,
  productsLoading,
  vendors,
  vendorsLoading,
  isSaving,
  error,
  onClose,
  onSave,
}) => {
  const isCreate = repairServiceType === null;

  const [productId, setProductId] = useState<string | null>(null);
  const [deviceType, setDeviceType] = useState<RepairDeviceType>(null);
  const [serviceType, setServiceType] = useState('');
  // Edited as a comma-separated string for low-friction entry. Round-trips to a
  // string[] on save.
  const [componentCategoriesText, setComponentCategoriesText] = useState('');
  const [requiresPart, setRequiresPart] = useState(false);
  // Default labour hours, edited as a string so the input is fully controlled
  // (empty + 0.25-step). Parsed to a number on save.
  const [defaultLabourHoursText, setDefaultLabourHoursText] = useState('1');
  const [isThirdParty, setIsThirdParty] = useState(false);
  const [thirdPartyVendorIds, setThirdPartyVendorIds] = useState<string[]>([]);
  const [isFixedPriceService, setIsFixedPriceService] = useState(false);
  // Third-party markup is always a fixed dollar amount added on top of the
  // vendor's cost ex-GST. String-backed so empty round-trips cleanly.
  const [markupValueText, setMarkupValueText] = useState('');
  // Slug of the quote terms template to pre-fill on the quote when this service
  // is the first one added. Null = no default.
  const [defaultQuoteTermsTemplateId, setDefaultQuoteTermsTemplateId] =
    useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);

  const { templates: termsTemplates, isLoading: termsTemplatesLoading } =
    useQuoteTermsTemplates();

  // Seed form state when the panel mounts / target changes.
  useEffect(() => {
    if (repairServiceType) {
      setProductId(repairServiceType.productId);
      setDeviceType(repairServiceType.deviceType);
      setServiceType(repairServiceType.serviceType);
      setComponentCategoriesText(
        (repairServiceType.componentCategories ?? []).join(', '),
      );
      // Fall back to the legacy "non-empty categories implies a part" rule so
      // rows created before this column existed keep the same behaviour until
      // the admin explicitly flips the switch.
      setRequiresPart(
        repairServiceType.requiresPart ??
          (repairServiceType.componentCategories?.length ?? 0) > 0,
      );
      setDefaultLabourHoursText(
        repairServiceType.defaultLabourUnits != null
          ? String(repairServiceType.defaultLabourUnits)
          : '1',
      );
      setIsThirdParty(Boolean(repairServiceType.isThirdParty));
      setThirdPartyVendorIds(repairServiceType.thirdPartyVendorIds ?? []);
      setIsFixedPriceService(Boolean(repairServiceType.isFixedPriceService));
      setMarkupValueText(
        repairServiceType.markupValue == null
          ? ''
          : String(repairServiceType.markupValue),
      );
      setDefaultQuoteTermsTemplateId(
        repairServiceType.defaultQuoteTermsTemplateId ?? null,
      );
      setIsActive(repairServiceType.isActive);
    } else {
      setProductId(null);
      setDeviceType(null);
      setServiceType('');
      setComponentCategoriesText('');
      setRequiresPart(false);
      setDefaultLabourHoursText('1');
      setIsThirdParty(false);
      setThirdPartyVendorIds([]);
      setIsFixedPriceService(false);
      setMarkupValueText('');
      setDefaultQuoteTermsTemplateId(null);
      setIsActive(true);
    }
  }, [repairServiceType]);

  // Escape closes the panel. (When a dropdown is open it consumes Escape and
  // stops propagation, so only the dropdown closes.)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Catalog products that don't have a Dataverse GUID (`id`) are useless as a
  // lookup target — filter them out so we don't try to save a row with a null
  // foreign key. NB: `Product.id` is the Dataverse GUID, NOT `Product.productId`
  // (the RepairShopr integer id) — the backend lookup binding needs the GUID.
  const productOptions = useMemo<SmartSelectOption[]>(
    () =>
      products
        .filter((p) => !!p.id)
        .map((p) => ({
          value: p.id,
          label: `${p.sku} — ${p.description || p.displayName}`,
        })),
    [products],
  );

  // Vendor list for the third-party picker — defensively filter for active +
  // isThirdPartyService. Any vendor that's currently selected but no longer
  // matches the filter is still surfaced so admins can see and clear stale
  // selections rather than silently losing them.
  const thirdPartyVendorOptions = useMemo(() => {
    const selectedSet = new Set(thirdPartyVendorIds);
    return vendors.filter(
      (v) =>
        (v.isActive && v.isThirdPartyService) || selectedSet.has(v.vendorId),
    );
  }, [vendors, thirdPartyVendorIds]);

  const selectedThirdPartyVendors = useMemo(
    () =>
      thirdPartyVendorIds
        .map((id) => thirdPartyVendorOptions.find((v) => v.vendorId === id))
        .filter((v): v is Vendor => !!v),
    [thirdPartyVendorOptions, thirdPartyVendorIds],
  );

  // Only un-selected vendors are addable through the picker.
  const addableVendorOptions = useMemo<SmartSelectOption[]>(() => {
    const selectedSet = new Set(thirdPartyVendorIds);
    return thirdPartyVendorOptions
      .filter((v) => !selectedSet.has(v.vendorId))
      .map((v) => ({ value: v.vendorId, label: `${v.name} (${v.code})` }));
  }, [thirdPartyVendorOptions, thirdPartyVendorIds]);

  const termsOptions = useMemo<SmartSelectOption[]>(() => {
    const opts: SmartSelectOption[] = termsTemplates.map((t) => ({
      value: t.slug,
      label: `${t.name}${t.isDefault ? ' (default)' : ''}`,
    }));
    // Surface a stored slug that no longer resolves (template deleted) so the
    // admin sees + can clear the stale value rather than it silently vanishing.
    if (
      defaultQuoteTermsTemplateId &&
      !opts.some((o) => o.value === defaultQuoteTermsTemplateId)
    ) {
      opts.unshift({
        value: defaultQuoteTermsTemplateId,
        label: `${defaultQuoteTermsTemplateId} (not found)`,
      });
    }
    // Explicit "None" so a chosen template can be cleared back to the quote's
    // normal default (SmartSelect has no clear affordance of its own).
    return [{ value: '', label: 'None (quote default)' }, ...opts];
  }, [termsTemplates, defaultQuoteTermsTemplateId]);

  const parsedMarkupValueRaw = Number(markupValueText);
  const markupValueValid =
    !isThirdParty ||
    markupValueText.trim() === '' ||
    (Number.isFinite(parsedMarkupValueRaw) && parsedMarkupValueRaw >= 0);

  const isValid =
    !!productId &&
    serviceType.trim().length > 0 &&
    (!isThirdParty || thirdPartyVendorIds.length > 0) &&
    markupValueValid;

  const handleSave = async () => {
    if (!isValid || isSaving) return;
    const categories = componentCategoriesText
      .split(',')
      .map((c) => c.trim())
      .filter((c) => c.length > 0);

    const parsedLabourHours = Number(defaultLabourHoursText);
    const defaultLabourUnits = Number.isFinite(parsedLabourHours)
      ? parsedLabourHours
      : 1;

    // Markup is always a dollar amount on top of the vendor's cost.
    let markupValue: number | null = null;
    if (isThirdParty && markupValueText.trim() !== '') {
      const v = Number(markupValueText);
      if (Number.isFinite(v)) {
        markupValue = v;
      }
    }

    const thirdPartyFields = {
      isThirdParty,
      thirdPartyVendorIds: isThirdParty ? thirdPartyVendorIds : [],
      markupType: isThirdParty && markupValue != null ? ('fixed' as const) : null,
      markupValue,
    };

    if (isCreate) {
      const payload: CreateRepairServiceTypeRequest = {
        productId: productId!,
        deviceType,
        serviceType: serviceType.trim(),
        componentCategories: categories,
        requiresPart,
        defaultLabourUnits,
        isFixedPriceService,
        defaultQuoteTermsTemplateId,
        isActive,
        ...thirdPartyFields,
      };
      await onSave(payload, null);
    } else {
      const payload: UpdateRepairServiceTypeRequest = {
        productId: productId ?? undefined,
        deviceType,
        serviceType: serviceType.trim(),
        componentCategories: categories,
        requiresPart,
        defaultLabourUnits,
        isFixedPriceService,
        defaultQuoteTermsTemplateId,
        isActive,
        ...thirdPartyFields,
      };
      await onSave(payload, repairServiceType!.id);
    }
  };

  return (
    <SidePanel
      title={isCreate ? 'Add repair service' : 'Edit repair service'}
      submitLabel={isSaving ? 'Saving…' : isCreate ? 'Create' : 'Save'}
      isValid={isValid && !isSaving}
      onClose={onClose}
      onSubmit={handleSave}
    >
      {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

      <div className="rsp-field">
        <SmartSelect
          label="Catalog product *"
          value={productId}
          options={productOptions}
          onChange={(v) => setProductId(v)}
          placeholder={productsLoading ? 'Loading…' : 'Select a product…'}
          disabled={productsLoading}
        />
        <HelpHint
          placement="left"
          text="The product's SKU becomes the customer-facing RCS.* code on quotes / invoices."
        />
      </div>

      <SmartSelect
        label="Device type"
        value={deviceType ?? ANY_DEVICE}
        options={DEVICE_TYPE_OPTIONS}
        onChange={(v) =>
          setDeviceType(v === ANY_DEVICE || v === null ? null : (v as RepairDeviceType))
        }
      />

      <div className="rsp-field">
        <FormField label="Service type *">
          <input
            value={serviceType}
            onChange={(e) => setServiceType(e.target.value)}
            placeholder="e.g. RAM, Battery, Screen, MBFlash"
          />
        </FormField>
        <HelpHint
          placement="left"
          text="Free-form discriminator paired with the device type to identify the repair."
        />
      </div>

      <div className="rsp-field">
        <FormField label="Component categories">
          <input
            value={componentCategoriesText}
            onChange={(e) => setComponentCategoriesText(e.target.value)}
            placeholder="e.g. ram, ssd"
          />
        </FormField>
        <HelpHint
          placement="left"
          text="Comma-separated. Soft-warns when a part on the quote doesn't match."
        />
      </div>

      <div className="rsp-field">
        <FormField label="Default labour hours">
          <input
            type="number"
            min={0}
            step={0.25}
            value={defaultLabourHoursText}
            onChange={(e) => setDefaultLabourHoursText(e.target.value)}
          />
        </FormField>
        <HelpHint
          placement="left"
          text="Seeds the labour line's Hours field when this service is added to a quote (0.25 step)."
        />
      </div>

      <div className="rsp-field">
        <SmartSelect
          label="Default quote terms template"
          value={defaultQuoteTermsTemplateId ?? ''}
          options={termsOptions}
          onChange={(v) => setDefaultQuoteTermsTemplateId(v ? v : null)}
          placeholder={termsTemplatesLoading ? 'Loading…' : 'Select…'}
          disabled={termsTemplatesLoading}
        />
        <HelpHint
          placement="left"
          text="Pre-fills the quote's terms template when this is the first service added (only if none is chosen yet). Leave blank to use the quote's normal default."
        />
      </div>

      <label className="side-panel__checkbox">
        <input
          type="checkbox"
          checked={requiresPart}
          onChange={(e) => setRequiresPart(e.target.checked)}
        />
        Auto-add part line on selection
      </label>

      {/* Third-party service — sub-contracted (specialist repair / data
          recovery). Mutually exclusive with fixed-price. */}
      <div className="rsp-toggle">
        <label className="side-panel__checkbox">
          <input
            type="checkbox"
            checked={isThirdParty}
            onChange={(e) => {
              const next = e.target.checked;
              setIsThirdParty(next);
              if (next) setIsFixedPriceService(false);
            }}
          />
          Third-party service
        </label>
        <HelpHint
          placement="left"
          text="Sub-contracted service (specialist repair, data recovery). When on, the quote-editor seeds a single third-party component instead of labour + part."
        />
      </div>

      {isThirdParty && (
        <>
          {selectedThirdPartyVendors.length > 0 && (
            <div className="rsp-vendors">
              {selectedThirdPartyVendors.map((v) => (
                <span key={v.vendorId} className="rsp-vendor-chip">
                  {v.name} ({v.code})
                  <button
                    type="button"
                    className="rsp-vendor-chip__remove"
                    aria-label={`Remove ${v.name}`}
                    onClick={() =>
                      setThirdPartyVendorIds((ids) =>
                        ids.filter((id) => id !== v.vendorId),
                      )
                    }
                  >
                    <IconX size={11} stroke={1.5} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="rsp-field">
            <SmartSelect
              label="Sub-contracted vendors *"
              value={null}
              options={addableVendorOptions}
              onChange={(v) => {
                if (v) setThirdPartyVendorIds((ids) => [...ids, v]);
              }}
              placeholder={vendorsLoading ? 'Loading…' : 'Add a vendor…'}
              disabled={vendorsLoading}
            />
            <HelpHint
              placement="left"
              text="Only vendors tagged as third-party service providers appear. A vendor-quote line is seeded for each when this service is added to a quote."
            />
          </div>
          <div className="rsp-field">
            <FormField label="Markup $ ex-GST">
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={0.01}
                value={markupValueText}
                onChange={(e) => setMarkupValueText(e.target.value)}
              />
            </FormField>
            <HelpHint
              placement="left"
              text="Dollar amount added on top of the vendor’s cost ex-GST."
            />
          </div>
          {!markupValueValid && (
            <p className="rsp-error">Markup must be a non-negative number.</p>
          )}
        </>
      )}

      {/* Fixed-price service — flat-rate offering with no labour/parts
          breakdown. Mutually exclusive with third-party. */}
      <div className="rsp-toggle">
        <label className="side-panel__checkbox">
          <input
            type="checkbox"
            checked={isFixedPriceService}
            disabled={isThirdParty}
            onChange={(e) => {
              const next = e.target.checked;
              setIsFixedPriceService(next);
              if (next) setIsThirdParty(false);
            }}
          />
          Fixed-price service
        </label>
        <HelpHint
          placement="left"
          text="Seeds a single fixed-price line driven by the catalog product price (overridable per quote). No labour / parts breakdown is required."
        />
      </div>

      <label className="side-panel__checkbox">
        <input
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
        />
        Active
      </label>
    </SidePanel>
  );
};
